import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Check, CheckCircle2, Undo2, Pause, Loader2,
  Plus, X, AlertTriangle, Trash2, Save,
} from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { SkillSelector } from "@/components/SkillSelector";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { EMPLOYMENT_TYPES, WORK_MODELS } from "@/lib/constants";
import { isCrawlerJob } from "@/lib/jobUtils";
import { AutoFilledLabel } from "@/components/admin/AutoFilledLabel";
import { ReviewContextSidebar } from "@/components/admin/ReviewContextSidebar";

export default function AdminRevisaoEditarVaga() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [requiredSkills, setRequiredSkills] = useState<string[]>([]);
  const [niceSkills, setNiceSkills] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [benefits, setBenefits] = useState<string[]>([]);
  const [newBenefit, setNewBenefit] = useState("");
  const [processSteps, setProcessSteps] = useState<string[]>([]);
  const [newStep, setNewStep] = useState("");

  const [formData, setFormData] = useState({
    titulo: "", tipo: "", modelo: "", cidade: "", uf: "", descricao: "",
    responsabilidades: "", requisitosObrigatorios: "", requisitosDesejaveis: "",
    prazo: "", stipendMin: "", stipendMax: "",
  });

  const { data: post, isLoading: postLoading } = useQuery({
    queryKey: ["review_job_detail", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("job_posts")
        .select("*, partner_orgs(name, is_auto_created), crawler_metadata(company_name, source_name, source_url, external_apply_url, source_type, crawler_score)")
        .eq("id", id!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (post && !loaded) {
      const p = post as any;
      setFormData({
        titulo: post.title || "",
        tipo: post.employment_type || "",
        modelo: post.work_model || "",
        cidade: post.location_city || "",
        uf: post.location_state || "",
        descricao: post.description || "",
        responsabilidades: Array.isArray(post.responsibilities) ? (post.responsibilities as string[]).join("\n") : "",
        requisitosObrigatorios: Array.isArray(post.requirements_must) ? (post.requirements_must as string[]).join("\n") : "",
        requisitosDesejaveis: Array.isArray(p.requirements_nice) ? (p.requirements_nice as string[]).join("\n") : "",
        prazo: post.deadline_date || "",
        stipendMin: p.stipend_min ? String(p.stipend_min) : "",
        stipendMax: p.stipend_max ? String(p.stipend_max) : "",
      });
      setRequiredSkills(Array.isArray(post.required_skills) ? (post.required_skills as string[]) : []);
      setNiceSkills(Array.isArray(post.nice_skills) ? (post.nice_skills as string[]) : []);
      setBenefits(Array.isArray(p.benefits) ? p.benefits : []);
      setProcessSteps(Array.isArray(p.process_steps) ? p.process_steps : []);
      setLoaded(true);
    }
  }, [post, loaded]);

  // Track which fields were auto-filled by crawler/enrichment
  const autoFilledFields = useMemo(() => {
    if (!post || !isCrawlerJob(post)) return new Set<string>();
    const p = post as any;
    const fields = new Set<string>();
    if (post.title) fields.add("titulo");
    if (post.employment_type) fields.add("tipo");
    if (post.work_model) fields.add("modelo");
    if (post.location_city) fields.add("cidade");
    if (post.location_state) fields.add("uf");
    if (post.description) fields.add("descricao");
    if (Array.isArray(post.responsibilities) && (post.responsibilities as string[]).length > 0) fields.add("responsabilidades");
    if (Array.isArray(post.requirements_must) && (post.requirements_must as string[]).length > 0) fields.add("requisitosObrigatorios");
    if (Array.isArray(p.requirements_nice) && p.requirements_nice.length > 0) fields.add("requisitosDesejaveis");
    if (Array.isArray(post.required_skills) && (post.required_skills as string[]).length > 0) fields.add("requiredSkills");
    if (Array.isArray(post.nice_skills) && (post.nice_skills as string[]).length > 0) fields.add("niceSkills");
    if (post.deadline_date) fields.add("prazo");
    if (p.stipend_min) fields.add("stipendMin");
    if (p.stipend_max) fields.add("stipendMax");
    if (Array.isArray(p.benefits) && p.benefits.length > 0) fields.add("benefits");
    if (Array.isArray(p.process_steps) && p.process_steps.length > 0) fields.add("processSteps");
    return fields;
  }, [post]);

  const af = (field: string) => autoFilledFields.has(field);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "requisitosObrigatorios") {
      const terms = ["anos de experiência", "experiência prévia", "experiência comprovada"];
      setShowWarning(terms.some((t) => value.toLowerCase().includes(t)));
    }
  };

  const addBenefit = () => { if (newBenefit.trim()) { setBenefits(prev => [...prev, newBenefit.trim()]); setNewBenefit(""); } };
  const addStep = () => { if (newStep.trim()) { setProcessSteps(prev => [...prev, newStep.trim()]); setNewStep(""); } };

  const buildPayload = () => ({
    title: formData.titulo,
    employment_type: formData.tipo || "aprendiz",
    work_model: formData.modelo || null,
    location_city: formData.cidade || null,
    location_state: formData.uf || null,
    description: formData.descricao || null,
    responsibilities: formData.responsabilidades ? formData.responsabilidades.split("\n").filter(Boolean) : null,
    requirements_must: formData.requisitosObrigatorios ? formData.requisitosObrigatorios.split("\n").filter(Boolean) : null,
    requirements_nice: formData.requisitosDesejaveis ? formData.requisitosDesejaveis.split("\n").filter(Boolean) : [],
    required_skills: requiredSkills.length > 0 ? requiredSkills : null,
    nice_skills: niceSkills.length > 0 ? niceSkills : null,
    deadline_date: formData.prazo || null,
    stipend_min: formData.stipendMin ? (parseInt(formData.stipendMin, 10) || null) : null,
    stipend_max: formData.stipendMax ? (parseInt(formData.stipendMax, 10) || null) : null,
    benefits: benefits.length > 0 ? benefits : [],
    process_steps: processSteps.length > 0 ? processSteps : [],
  });

  const handleSave = async () => {
    if (!id) return;
    if (!formData.titulo.trim()) {
      toast({ title: "Título é obrigatório", variant: "destructive" });
      return;
    }
    setIsLoading(true);
    try {
      const payload = buildPayload();
      const { error } = await supabase.from("job_posts").update(payload).eq("id", id);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ["admin_vagas"] });
      queryClient.invalidateQueries({ queryKey: ["admin_all_jobs"] });
      queryClient.invalidateQueries({ queryKey: ["review_job_detail", id] });
      toast({ title: "Alterações salvas" });
      navigate("/admin/vagas");
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = async (newStatus: string) => {
    if (!id) return;
    if (!formData.titulo.trim()) {
      toast({ title: "Título é obrigatório", variant: "destructive" });
      return;
    }
    setIsLoading(true);
    try {
      const payload: any = buildPayload();
      payload.status = newStatus;
      if (newStatus === "publicada") payload.published_at = new Date().toISOString();
      const { error } = await supabase.from("job_posts").update(payload).eq("id", id);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ["admin_vagas"] });
      queryClient.invalidateQueries({ queryKey: ["admin_all_jobs"] });
      queryClient.invalidateQueries({ queryKey: ["admin_active_jobs"] });
      const msgs: Record<string, string> = {
        publicada: "Vaga publicada com sucesso!",
        rascunho: "Vaga devolvida para enriquecimento",
        pausada: "Vaga pausada",
        descartada: "Vaga descartada",
      };
      toast({ title: msgs[newStatus] ?? "Status atualizado" });
      navigate("/admin/vagas");
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  if (postLoading) {
    return <div className="p-8 flex justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }
  if (!post) {
    return <div className="p-8 text-center"><p className="text-muted-foreground">Vaga não encontrada.</p></div>;
  }

  const p = post as any;
  const meta = p.crawler_metadata?.[0];
  const orgName = p.partner_orgs?.name ?? "—";

  return (
    <div className="p-8 max-w-6xl">
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate("/admin/vagas")}><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-bold text-foreground">Revisão de Vaga</h1>
          <p className="text-muted-foreground text-sm">Revise, edite e decida sobre a publicação — Org: {orgName}</p>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
        {/* Main form column */}
        <form onSubmit={(e) => e.preventDefault()} className="space-y-8">
        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <h2 className="font-heading font-semibold text-foreground mb-4">Informações Básicas</h2>
          <div className="space-y-2">
            <AutoFilledLabel htmlFor="titulo" isAutoFilled={af("titulo")}>Título da vaga *</AutoFilledLabel>
            <Input id="titulo" value={formData.titulo} onChange={(e) => handleChange("titulo", e.target.value)} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <AutoFilledLabel isAutoFilled={af("tipo")}>Tipo *</AutoFilledLabel>
              <div className="flex flex-wrap gap-2">
                {EMPLOYMENT_TYPES.map((t) => (
                  <Badge key={t.value} variant={formData.tipo === t.value ? "interest-selected" : "interest"} className="cursor-pointer" onClick={() => handleChange("tipo", t.value)}>
                    {formData.tipo === t.value && <Check className="w-3 h-3" />} {t.label}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <AutoFilledLabel isAutoFilled={af("modelo")}>Modelo *</AutoFilledLabel>
              <div className="flex flex-wrap gap-2">
                {WORK_MODELS.map((m) => (
                  <Badge key={m.value} variant={formData.modelo === m.value ? "interest-selected" : "interest"} className="cursor-pointer" onClick={() => handleChange("modelo", m.value)}>
                    {formData.modelo === m.value && <Check className="w-3 h-3" />} {m.label}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          {formData.modelo !== "remoto" && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><AutoFilledLabel isAutoFilled={af("cidade")}>Cidade</AutoFilledLabel><Input value={formData.cidade} onChange={(e) => handleChange("cidade", e.target.value)} /></div>
              <div className="space-y-2"><AutoFilledLabel isAutoFilled={af("uf")}>UF</AutoFilledLabel><Input maxLength={2} value={formData.uf} onChange={(e) => handleChange("uf", e.target.value.toUpperCase())} /></div>
            </div>
          )}
        </div>

        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <h2 className="font-heading font-semibold text-foreground mb-4">Descrição</h2>
          <AutoFilledLabel isAutoFilled={af("descricao")}>Descrição da vaga</AutoFilledLabel>
          <Textarea placeholder="Descreva a vaga..." value={formData.descricao} onChange={(e) => handleChange("descricao", e.target.value)} rows={4} />
          <AutoFilledLabel isAutoFilled={af("responsabilidades")}>Responsabilidades (uma por linha)</AutoFilledLabel>
          <Textarea placeholder="Responsabilidades..." value={formData.responsabilidades} onChange={(e) => handleChange("responsabilidades", e.target.value)} rows={3} />
        </div>

        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <h2 className="font-heading font-semibold text-foreground mb-4">Requisitos</h2>
          <AutoFilledLabel isAutoFilled={af("requisitosObrigatorios")}>Requisitos obrigatórios (um por linha)</AutoFilledLabel>
          <Textarea value={formData.requisitosObrigatorios} onChange={(e) => handleChange("requisitosObrigatorios", e.target.value)} rows={3} />
          {showWarning && (
            <div className="bg-warning/10 rounded-xl p-4 border border-warning/20 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-warning shrink-0" />
              <p className="text-sm font-medium text-foreground">Evite exigir experiência prévia</p>
            </div>
          )}
          <AutoFilledLabel isAutoFilled={af("requisitosDesejaveis")}>Requisitos desejáveis (um por linha)</AutoFilledLabel>
          <Textarea value={formData.requisitosDesejaveis} onChange={(e) => handleChange("requisitosDesejaveis", e.target.value)} rows={2} />
        </div>

        <div className="bg-card rounded-2xl p-6 border border-border space-y-6">
          <h2 className="font-heading font-semibold text-foreground mb-2">Habilidades</h2>
          <SkillSelector selectedSkills={requiredSkills} onChange={setRequiredSkills} maxSkills={6} label="Obrigatórias (até 6)" />
          <SkillSelector selectedSkills={niceSkills} onChange={(s) => setNiceSkills(s.filter((x) => !requiredSkills.includes(x)))} maxSkills={8} label="Desejáveis (até 8)" />
        </div>

        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <h2 className="font-heading font-semibold text-foreground mb-4">Remuneração e Benefícios</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><AutoFilledLabel isAutoFilled={af("stipendMin")}>Bolsa mínima (R$)</AutoFilledLabel><Input type="number" value={formData.stipendMin} onChange={(e) => handleChange("stipendMin", e.target.value)} /></div>
            <div className="space-y-2"><AutoFilledLabel isAutoFilled={af("stipendMax")}>Bolsa máxima (R$)</AutoFilledLabel><Input type="number" value={formData.stipendMax} onChange={(e) => handleChange("stipendMax", e.target.value)} /></div>
          </div>
          <div className="space-y-2">
            <AutoFilledLabel isAutoFilled={af("benefits")}>Benefícios</AutoFilledLabel>
            <div className="flex gap-2">
              <Input placeholder="Ex: Vale-transporte" value={newBenefit} onChange={(e) => setNewBenefit(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBenefit(); } }} />
              <Button type="button" variant="outline" size="icon" onClick={addBenefit}><Plus className="w-4 h-4" /></Button>
            </div>
            {benefits.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {benefits.map((b, i) => (<Badge key={i} variant="secondary" className="gap-1">{b} <X className="w-3 h-3 cursor-pointer" onClick={() => setBenefits(prev => prev.filter((_, j) => j !== i))} /></Badge>))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <h2 className="font-heading font-semibold text-foreground mb-4">Etapas do Processo Seletivo</h2>
          <div className="flex gap-2">
            <Input placeholder="Ex: Inscrição online" value={newStep} onChange={(e) => setNewStep(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addStep(); } }} />
            <Button type="button" variant="outline" size="icon" onClick={addStep}><Plus className="w-4 h-4" /></Button>
          </div>
          {processSteps.length > 0 && (
            <ol className="space-y-2 mt-2">
              {processSteps.map((s, i) => (
                <li key={i} className="flex items-center gap-3 text-sm">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <span className="flex-1 text-foreground">{s}</span>
                  <X className="w-4 h-4 text-muted-foreground cursor-pointer" onClick={() => setProcessSteps(prev => prev.filter((_, j) => j !== i))} />
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <AutoFilledLabel isAutoFilled={af("prazo")}>Prazo para candidatura</AutoFilledLabel>
          <Input type="date" value={formData.prazo} onChange={(e) => handleChange("prazo", e.target.value)} />
        </div>

        {/* Action bar */}
        <div className="flex gap-4 flex-wrap sticky bottom-4 bg-background/95 backdrop-blur rounded-2xl p-4 border border-border shadow-lg">
          <Button
            type="button"
            variant="destructive"
            size="lg"
            disabled={isLoading}
            onClick={() => {
              if (window.confirm("Tem certeza que deseja descartar esta vaga?")) handleAction("descartada");
            }}
          >
            <Trash2 className="w-4 h-4" /> Descartar
          </Button>
          <Button
            type="button"
            variant="outline"
            size="lg"
            disabled={isLoading}
            onClick={() => handleAction("rascunho")}
            title="Devolver para enriquecimento"
          >
            <Undo2 className="w-4 h-4" /> Devolver p/ enriquecimento
          </Button>
          <div className="flex-1" />
          <Button
            type="button"
            variant="outline"
            size="lg"
            disabled={isLoading}
            onClick={() => handleAction("pausada")}
          >
            <Pause className="w-4 h-4" /> Pausar
          </Button>
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={() => navigate("/admin/vagas")}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            disabled={isLoading}
            onClick={handleSave}
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar
          </Button>
          <Button
            type="button"
            variant="hero"
            size="lg"
            disabled={isLoading}
            onClick={() => handleAction("publicada")}
          >
            <CheckCircle2 className="w-4 h-4" /> Publicar
          </Button>
        </div>
      </form>

        {/* Context sidebar */}
        <aside className="hidden lg:block">
          <ReviewContextSidebar post={post} meta={meta} orgName={orgName} />
        </aside>
      </div>
    </div>
  );
}
